// =============================================================================
// Share-by-URL: encode the calculator's state into a query string, and decode
// incoming URLs back into inputs/settings so a shared link reproduces the same
// view on the recipient's screen.
//
// Param keys are short to keep URLs compact:
//   p   price           s   shipping (charged)   sc  shippingCost (paid)
//   c   materials       q   quantity             d   discount
//   dt  discountType    lh  laborHours           lr  laborRate
//   pkg packaging       rr  returnRate
//   co  country         oa  offsiteAds (0|1)     or  offsiteRate
//   ot  orderType (s|b) dc  displayCurrency
// =============================================================================

const INPUT_MAP = {
  p:   'price',
  s:   'shipping',
  sc:  'shippingCost',
  c:   'cost',
  q:   'quantity',
  d:   'discount',
  dt:  'discountType',
  lh:  'laborHours',
  lr:  'laborRate',
  pkg: 'packaging',
  rr:  'returnRate',
};

const SETTING_MAP = {
  co: 'country',
  oa: 'offsiteEnabled',
  or: 'offsiteRate',
  ot: 'orderType',
  dc: 'displayCurrency',
  tx: 'taxRate',
  pc: 'productCategory',
};

/**
 * Encode current inputs + settings into a URL search string.
 * Empty / default values are omitted to keep the URL short.
 */
export function encodeShareUrl(inputs, settings, baseUrl) {
  const params = new URLSearchParams();

  // Inputs — only include if non-empty/non-zero
  Object.entries(INPUT_MAP).forEach(([key, prop]) => {
    const v = inputs?.[prop];
    if (v === undefined || v === null || v === '') return;
    if (prop === 'discountType') {
      // Encode as 'd' or 'p' for compactness
      if (v === 'percent') params.set(key, 'p');
      // dollar is the default — skip
      return;
    }
    if (prop === 'quantity' && (v === 1 || v === '1')) return;  // default qty
    params.set(key, String(v));
  });

  // Settings
  if (settings?.country && settings.country !== 'US') params.set('co', settings.country);
  if (settings?.offsiteEnabled) {
    params.set('oa', '1');
    if (settings.offsiteRate && settings.offsiteRate !== 0.15) {
      params.set('or', String(settings.offsiteRate));
    }
  }
  if (settings?.orderType === 'bulk') params.set('ot', 'b');
  if (settings?.displayCurrency && settings.displayCurrency !== 'USD') {
    params.set('dc', settings.displayCurrency);
  }
  if (settings?.taxRate && settings.taxRate > 0) {
    params.set('tx', String(settings.taxRate));
  }
  if (settings?.productCategory && settings.productCategory !== 'none') {
    params.set('pc', settings.productCategory);
  }

  const base = baseUrl
    || (typeof window !== 'undefined'
          ? window.location.origin + window.location.pathname
          : '');
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Decode a URLSearchParams (or a Location's search) back into partial
 * { inputs, settings } objects. Caller spreads these onto the existing state.
 */
export function decodeShareUrl(searchParams) {
  if (typeof searchParams === 'string') {
    searchParams = new URLSearchParams(searchParams);
  }
  const inputs = {};
  const settings = {};

  Object.entries(INPUT_MAP).forEach(([key, prop]) => {
    if (!searchParams.has(key)) return;
    const raw = searchParams.get(key);
    if (prop === 'discountType') {
      inputs[prop] = raw === 'p' ? 'percent' : 'dollar';
    } else {
      inputs[prop] = raw;
    }
  });

  if (searchParams.has('co')) settings.country = searchParams.get('co');
  if (searchParams.has('oa')) {
    settings.offsiteEnabled = searchParams.get('oa') === '1';
    if (searchParams.has('or')) {
      const r = parseFloat(searchParams.get('or'));
      if (!isNaN(r)) settings.offsiteRate = r;
    }
  }
  if (searchParams.has('ot')) {
    settings.orderType = searchParams.get('ot') === 'b' ? 'bulk' : 'separate';
  }
  if (searchParams.has('dc')) settings.displayCurrency = searchParams.get('dc');
  if (searchParams.has('tx')) {
    const t = parseFloat(searchParams.get('tx'));
    if (!isNaN(t)) settings.taxRate = t;
  }
  if (searchParams.has('pc')) settings.productCategory = searchParams.get('pc');

  return { inputs, settings };
}

/**
 * Detect whether the current page URL has any share params at all.
 */
export function hasShareParams(searchString) {
  const params = new URLSearchParams(
    searchString || (typeof window !== 'undefined' ? window.location.search : '')
  );
  for (const key of Object.keys(INPUT_MAP)) if (params.has(key)) return true;
  for (const key of Object.keys(SETTING_MAP)) if (params.has(key)) return true;
  return false;
}
